/**
 * CGM-OPERATIONAL-CONTROL-R3 — safe parcel valve command machine.
 *
 * The browser supplies only binding_id + semantic action.  It never supplies a
 * physical channel. One confirmed intent creates one POST with one stable
 * idempotency key; there is no automatic retry.  Every ambiguous state fails
 * closed until exact command/physical feedback is observed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, iotApi, type Command, type IoTNode, type ParcelValveBinding } from '../lib/iotApi';
import { canIssuePhysicalCommands } from '../auth/cognito';
import {
  targetSnapshotTimestampMs,
  isBusyStatus,
  isRefreshProven,
  isTerminalCommandStatus,
  type RefreshProof,
} from '../lib/valveCommandSafety';

export interface PendingParcelConfirmation {
  binding: ParcelValveBinding;
  action: 'open' | 'close';
  idempotencyKey: string;
}

export type ParcelCommandOutcome =
  | 'unassigned'
  | 'semantic_binding_required'
  | 'commissioning_incomplete'
  | 'actuation_not_authorized'
  | 'wiring_not_confirmed'
  | 'conflict'
  | 'forbidden'
  | 'uncertain'
  | null;

const UNCERTAIN_OUTCOME: ParcelCommandOutcome = 'uncertain';

let idempotencySeq = 0;
const generateIdempotencyKey = (): string => {
  idempotencySeq += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `parcel-${Date.now().toString(36)}-${idempotencySeq.toString(36)}-${rand}`;
};

const POLL_MS = 5000;
const MAX_POLLS = 12;

interface PendingRefreshProof {
  proof: RefreshProof;
  commandId: string;
}

export interface UseParcelValveCommandOptions {
  role: string | null | undefined;
  nodes?: IoTNode[];
  refreshNodes: () => Promise<IoTNode[]>;
}

export interface UseParcelValveCommandResult {
  pending: PendingParcelConfirmation | null;
  statusFor: (bindingId: string) => string | undefined;
  outcomeFor: (bindingId: string) => ParcelCommandOutcome;
  requestCommand: (binding: ParcelValveBinding, action: 'open' | 'close') => void;
  cancelConfirmation: () => void;
  confirmCommand: () => Promise<void>;
}

const outcomeFromApiError = (err: ApiError): ParcelCommandOutcome => {
  const detail = err.detail || '';
  if (detail.includes('UNASSIGNED_CHANNEL_LOCKED')) return 'unassigned';
  if (detail.includes('SEMANTIC_PARCEL_BINDING_REQUIRED')) return 'semantic_binding_required';
  if (detail.includes('COMMISSIONING_NOT_COMPLETE')) return 'commissioning_incomplete';
  if (detail.includes('ACTUATION_NOT_AUTHORIZED')) return 'actuation_not_authorized';
  if (detail.includes('WIRING_NOT_CONFIRMED')) return 'wiring_not_confirmed';
  if (detail.includes('TTN_OUTCOME_UNCERTAIN')) return 'uncertain';
  if (err.semanticCode === 'forbidden' || err.httpStatus === 403) return 'forbidden';
  if (err.semanticCode === 'conflict' || err.httpStatus === 409) return 'conflict';
  return 'uncertain';
};

export function useParcelValveCommand(
  options: UseParcelValveCommandOptions,
): UseParcelValveCommandResult {
  const { role, nodes = [], refreshNodes } = options;
  const [pending, setPending] = useState<PendingParcelConfirmation | null>(null);
  const [status, setStatus] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<Record<string, ParcelCommandOutcome>>({});
  const pendingRef = useRef<PendingParcelConfirmation | null>(null);
  const statusRef = useRef<Record<string, string>>({});
  const activeCommandRef = useRef<Record<string, string>>({});
  const refreshProofRef = useRef<Record<string, PendingRefreshProof>>({});
  const issuedAfterRef = useRef<Record<string, number>>({});
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const pollTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pollCountRef = useRef<Record<string, number>>({});
  const canControl = canIssuePhysicalCommands(role);
  const deniedBindingsRef = useRef(new Set<string>());

  const setStatusFor = useCallback((bindingId: string, st: string | null, commandId?: string) => {
    const next = { ...statusRef.current };
    const nextCmd = { ...activeCommandRef.current };
    if (st === null) {
      delete next[bindingId];
      delete nextCmd[bindingId];
    } else {
      next[bindingId] = st;
      if (commandId) nextCmd[bindingId] = commandId;
    }
    statusRef.current = next;
    activeCommandRef.current = nextCmd;
    setStatus(next);
  }, []);

  useEffect(() => {
    const timers = pollTimersRef.current;
    return () => Object.values(timers).forEach((timer) => clearTimeout(timer));
  }, []);

  // React state commit is part of the proof boundary: even when refreshNodes()
  // already returned proving data, keep the control REFRESHING until the page's
  // rendered node snapshot contains that exact physical state.
  useEffect(() => {
    for (const [bindingId, pendingProof] of Object.entries(refreshProofRef.current)) {
      const node = nodes.find((candidate) => candidate.id === pendingProof.proof.nodeId);
      if (!isRefreshProven(pendingProof.proof, node)) continue;
      delete refreshProofRef.current[bindingId];
      setStatusFor(bindingId, 'OBSERVED_SUCCESS', pendingProof.commandId);
      setOutcome((prev) => ({ ...prev, [bindingId]: null }));
    }
  }, [nodes, setStatusFor]);

  const holdForPhysicalProof = useCallback((
    binding: ParcelValveBinding,
    action: 'open' | 'close',
    commandId: string,
  ) => {
    const bindingId = binding.binding_id;
    const proof: RefreshProof = {
      kind: 'expectedState',
      nodeId: binding.controller_node_id,
      channelId: binding.channel_id,
      value: action === 'open' ? 1 : 0,
      afterTimestamp: issuedAfterRef.current[bindingId] ?? Number.POSITIVE_INFINITY,
    };
    refreshProofRef.current[bindingId] = { proof, commandId };
    setStatusFor(bindingId, 'REFRESHING', commandId);
    setOutcome((prev) => ({ ...prev, [bindingId]: null }));

    refreshNodes()
      .then((rows) => {
        const stillPending = refreshProofRef.current[bindingId];
        if (!stillPending || stillPending.commandId !== commandId) return;
        const refreshedNode = rows.find((candidate) => candidate.id === proof.nodeId);
        if (isRefreshProven(proof, refreshedNode)) {
          // Do not unlock here. The nodes useEffect above unlocks only after the
          // same proving snapshot has reached rendered React state.
          return;
        }
        delete refreshProofRef.current[bindingId];
        setStatusFor(bindingId, 'UNCERTAIN', commandId);
        setOutcome((prev) => ({ ...prev, [bindingId]: 'uncertain' }));
      })
      .catch(() => {
        const stillPending = refreshProofRef.current[bindingId];
        if (!stillPending || stillPending.commandId !== commandId) return;
        delete refreshProofRef.current[bindingId];
        setStatusFor(bindingId, 'UNCERTAIN', commandId);
        setOutcome((prev) => ({ ...prev, [bindingId]: 'uncertain' }));
      });
  }, [refreshNodes, setStatusFor]);

  const pollExactCommand = useCallback((
    binding: ParcelValveBinding,
    action: 'open' | 'close',
    commandId: string,
  ) => {
    const bindingId = binding.binding_id;
    const count = (pollCountRef.current[bindingId] ?? 0) + 1;
    pollCountRef.current[bindingId] = count;
    if (count > MAX_POLLS) {
      setStatusFor(bindingId, 'UNCERTAIN', commandId);
      setOutcome((prev) => ({ ...prev, [bindingId]: 'uncertain' }));
      return;
    }

    iotApi.getJson<Command[]>(
      `/iot/commands?command_ids=${encodeURIComponent(commandId)}&node_ids=${encodeURIComponent(binding.controller_node_id)}`,
    ).then((rows) => {
      const list = (Array.isArray(rows) ? rows : []).map((command) => ({
        ...command,
        id: command.command_id ?? command.id,
      }));
      const command = list.find((candidate) => candidate.id === commandId);
      if (!command) {
        setStatusFor(bindingId, 'UNCERTAIN', commandId);
        setOutcome((prev) => ({ ...prev, [bindingId]: 'uncertain' }));
        return;
      }
      const nextStatus = typeof command.status === 'string' ? command.status : 'UNCERTAIN';
      if (nextStatus === 'OBSERVED_SUCCESS') {
        holdForPhysicalProof(binding, action, commandId);
        return;
      }
      setStatusFor(bindingId, nextStatus, commandId);
      if (isTerminalCommandStatus(nextStatus)) return;
      pollTimersRef.current[bindingId] = setTimeout(
        () => pollExactCommand(binding, action, commandId),
        POLL_MS,
      );
    }).catch(() => {
      setStatusFor(bindingId, 'UNCERTAIN', commandId);
      setOutcome((prev) => ({ ...prev, [bindingId]: 'uncertain' }));
    });
  }, [holdForPhysicalProof, setStatusFor]);

  const requestCommand = useCallback((binding: ParcelValveBinding, action: 'open' | 'close') => {
    if (deniedBindingsRef.current.has(binding.binding_id) || !binding.command_authorized || !canControl || isBusyStatus(statusRef.current[binding.binding_id])) return;
    const confirmation = {
      binding,
      action,
      idempotencyKey: generateIdempotencyKey(),
    } satisfies PendingParcelConfirmation;
    pendingRef.current = confirmation;
    setPending(confirmation);
  }, [canControl]);

  const cancelConfirmation = useCallback(() => {
    pendingRef.current = null;
    setPending(null);
  }, []);

  const confirmCommand = useCallback(async () => {
    const confirmation = pendingRef.current;
    if (!confirmation) return;
    const { binding, action, idempotencyKey } = confirmation;
    pendingRef.current = null;
    setPending(null);

    if (deniedBindingsRef.current.has(binding.binding_id) || !canIssuePhysicalCommands(role) || !binding.command_authorized) {
      setOutcome((prev) => ({ ...prev, [binding.binding_id]: 'forbidden' }));
      return;
    }

    const node = nodesRef.current.find(candidate => candidate.id === binding.controller_node_id);
    // Require a target snapshot newer than the pre-command observation and no
    // earlier than submission. A matching old value is not physical evidence.
    issuedAfterRef.current[binding.binding_id] = Math.max(
      Date.now() - 1,
      node ? targetSnapshotTimestampMs(node, binding.channel_id) ?? 0 : 0,
    );
    setStatusFor(binding.binding_id, 'REQUESTED');
    setOutcome((prev) => ({ ...prev, [binding.binding_id]: null }));
    try {
      const response = await iotApi.post(
        `/iot/parcel-valve-bindings/${encodeURIComponent(binding.binding_id)}/command`,
        { action },
        idempotencyKey,
      );
      const commandId = typeof response?.command_id === 'string' && response.command_id.trim()
        ? response.command_id
        : undefined;
      const nextStatus = typeof response?.status === 'string' ? response.status : 'REQUESTED';
      if (!commandId) {
        setStatusFor(binding.binding_id, 'UNCERTAIN');
        setOutcome((prev) => ({ ...prev, [binding.binding_id]: 'uncertain' }));
        return;
      }
      if (nextStatus === 'OBSERVED_SUCCESS') {
        holdForPhysicalProof(binding, action, commandId);
        return;
      }
      setStatusFor(binding.binding_id, nextStatus, commandId);
      if (isTerminalCommandStatus(nextStatus)) return;
      pollCountRef.current[binding.binding_id] = 0;
      pollExactCommand(binding, action, commandId);
    } catch (err) {
      delete refreshProofRef.current[binding.binding_id];
      const mapped = err instanceof ApiError ? outcomeFromApiError(err) : UNCERTAIN_OUTCOME;
      if (mapped === 'forbidden') {
        deniedBindingsRef.current.add(binding.binding_id);
        setStatusFor(binding.binding_id, null);
      }
      else if (mapped === 'conflict') setStatusFor(binding.binding_id, 'REQUESTED');
      else setStatusFor(binding.binding_id, 'UNCERTAIN');
      setOutcome((prev) => ({ ...prev, [binding.binding_id]: mapped }));
    }
  }, [role, setStatusFor, holdForPhysicalProof, pollExactCommand]);

  return {
    pending,
    statusFor: (bindingId) => status[bindingId],
    outcomeFor: (bindingId) => outcome[bindingId] ?? null,
    requestCommand,
    cancelConfirmation,
    confirmCommand,
  };
}
